import { LineItem } from '@hotjar/webapp/billing/subscription';
import { mapPlanModelToDto } from '@hotjar/webapp/domains/revenue/plan/mapper';
import type { Plan } from '@hotjar/webapp/domains/revenue/plan/types';
import type { ComponentsDeduction, Customer } from './customer.types';

interface EntityConfig {
  customer: Customer | null;
  entity: { name: string; id: number; organizationId: number; plan: Plan };
}

const DeductionTypeName: Record<string, string> = {
  AGENCY: 'Agency discount',
  LEGACY: 'Legacy discount',
  AD_HOC_PERCENTAGE: 'Ad hoc percentage discount',
  AD_HOC_MONETARY: 'Ad hoc monetary discount',
};

export const mapCustomerEntityToLegacyPriceSummaryLineItem = ({
  customer,
  entity: { id, name, organizationId, plan },
}: EntityConfig): LineItem | null => {
  if (!customer) {
    return LineItem.fromResponse({
      base_cost_per_unit: '0',
      cost_per_unit: '0',
      subtotal: '0',
      total: '0',
      units: 1,
      deductions: [],
      site_plan_info: {
        plan: mapPlanModelToDto(plan),
        site_reference: {
          site_id: id,
          name,
          organization_id: organizationId,
        },
      },
    });
  }

  const purchasedProductCost = customer.currentPeriodCostSummary.purchasedProductCosts.find(
    ({ entity }) => entity.entityIdentifier === id.toString(),
  );

  const subscriptionProduct = customer.subscription.products.find(
    ({ entity }) => entity.entityIdentifier === id.toString(),
  );

  const sessions = purchasedProductCost?.componentCosts.find(
    ({ componentIdentifier }) => componentIdentifier === 'SESSIONS',
  );

  const availableDeductions = [
    ...(subscriptionProduct?.componentsMonetaryDeduction ?? []),
    ...(subscriptionProduct?.componentsPercentageDeduction ?? []),
  ];

  const subtotal = (sessions?.amountWithoutTax ?? 0).toString();
  const total = (purchasedProductCost?.amountWithoutTax ?? 0).toString();

  return LineItem.fromResponse({
    // NOTE: base_cost_per_unit and cost_per_unit are wrong (same in preview endpoint though), but also not used anywhere
    // and we will replace the preview endpoint with zuora implementation soooo ¯\_(ツ)_/¯
    base_cost_per_unit: subtotal,
    cost_per_unit: total,
    subtotal,
    total,
    // NOTE: units is wrong as customer does not have a concept of units, but it is also not used soooooo ¯\_(ツ)_/¯
    units: 1,
    deductions: (purchasedProductCost?.componentCosts ?? [])
      // filter out items that are not deductions
      .filter(({ componentIdentifier }) =>
        availableDeductions.find(({ identifier }) => componentIdentifier === identifier),
      )
      .map(({ componentIdentifier, amountWithoutTax }) => {
        const componentCost = availableDeductions.find(
          ({ identifier }) => componentIdentifier === identifier,
        ) as ComponentsDeduction; // we cast here because componentCost will always exist after filtering

        return {
          amount: amountWithoutTax.toString(),
          deduction: {
            id: componentIdentifier,
            name: DeductionTypeName[componentIdentifier] ?? componentIdentifier,
            application: componentCost.applicationType,
            ...(componentCost.percentage && { percentage: componentCost?.percentage.toString() }),
            ...(componentCost.amount && { amount: componentCost.amount.toString() }),
          },
        };
      }),
    site_plan_info: {
      plan: mapPlanModelToDto(plan),
      site_reference: {
        site_id: id,
        name,
        organization_id: organizationId,
      },
    },
  });
};
